import { useState, useEffect, useRef } from 'react';
import './styles.css';

/* Hi developer! If you're reading this, please remember that I'm an embedded software developer. Don't be too hard on me; I only develop web apps for fun :) */

export default function TerminalPortfolio() {
  const initialHistory = [
    { type: 'output', text: "PATI I'M. Welcome to my digital portfolio. v1.0.0.", italic: true },
    { type: 'output', text: "Type 'help' and press 'enter' to view available commands.", italic: true }
  ];

  const [input, setInput] = useState('');
  const [history, setHistory] = useState(initialHistory);
  const [isMobile, setIsMobile] = useState(false);
  const inputRef = useRef(null);
  const terminalRef = useRef(null);
  
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  const availableCommands = ['help', 'about', 'skills', 'experience', 'education', 'contact', 'clear', 'all'];

const bioData = {
  name: "Jacopo Pati",
  title: "Embedded Software Developer",
  company: "Thales Alenia Space",
  education: "Master's Degree in Computer Engineering - Politecnico di Torino",
  skills: [
    "C", "C++", "Python", "RTEMS", "Shell scripting",
    "Embedded Systems", "Real-Time Systems", "Assembler",
    "SQL", "Java", "JavaScript", "React", "HTML/CSS"
    
  ],
  experience: [
    {
      company: "Thales Alenia Space",
      role: "Embedded Software Developer",
      period: "2024 - Present",
      description: "On-Board Software Discipline. Working on the ExoMars Rover and EDLM mission, focusing on real-time embedded systems development."
    },
    {
      company: "Politecnico di Torino / Thales Alenia Space",
      role: "Research Fellow",
      period: "2023 - 2024",
      description: "Developed a modular and reusable real-time system for novel avionic architectures for space modules, mainly using C and RTEMS, and implementing communications over MIL-STD-1553, CAN bus, and Ethernet. Paper presented at DASIA 2024 in Croatia."
    },
    {
      company: "Synopsys Inc.",
      role: "Intern",
      period: "2022 - 2023",
      description: "Research on automatic methods to detect GPU-friendly algorithms in large codebases, and ported parts of OpenTimer to GPU using OpenACC."
    }
  ],
  /*
  projects: [
    {
      name: "Modular Avionics for Habitable Habitats",
      description: "Development of a flexible real-time system architecture for future space modules using C and RTEMS, with robust module communications. Paper presented ad DASIA 2024."
    },
    {
      name: "GPU Acceleration Research",
      description: "Analysis and porting of computational kernels for GPU execution in EDA tools using OpenACC and performance profiling tools."
    }
  ],
  */
  contact: {
    email: "jacopopati@gmail.com",
    linkedin: "linkedin.com/in/jacopopati"
  }
};

useEffect(() => {
  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };
  
  checkMobile();
  window.addEventListener('resize', checkMobile);
  
  return () => {
    window.removeEventListener('resize', checkMobile);
  };
}, []);

const handleCommand = (cmd) => {
  const commandList = {
    'help': () => [
      "Available commands:",
      "- about      : Who I am",
      "- skills     : My technical skills",
      "- experience : My work experience",
      "- education  : My educational background",
      //"- projects   : My main projects",
      "- contact    : Contact information",
      "- clear      : Clear the screen",
      "- all        : Show all information"
    ],
    'about': () => [
      `Name: ${bioData.name}`,
      `Role: ${bioData.title}`,
      `Company: ${bioData.company}`,
      "\nSoftware engineer passionate about embedded systems, space technologies, and real-time architectures. Eager to innovate and continuously grow."
    ],
    'skills': () => [
      "Technical Skills:",
      ...bioData.skills.map(skill => `- ${skill}`)
    ],
    'experience': () => [
      "Work Experience:",
      ...bioData.experience.map(job => 
        `\n${job.role} @ ${job.company} (${job.period})\n${job.description}`
      )
    ],
    'education': () => [
      "Education:",
      bioData.education,
      "\nSpecialization in Software Engineering and Real-Time Systems."
    ],
    /*
    'projects': () => [
      "Main Projects:",
      ...bioData.projects.map(project => 
        `\n${project.name}\n${project.description}`
      )
    ],
    */
    'contact': () => [
      "Contact:",
      `Email: <a href="mailto:${bioData.contact.email}" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">${bioData.contact.email}</a>`,
      `LinkedIn: <a href="https://${bioData.contact.linkedin}" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">${bioData.contact.linkedin}</a>`
    ],
    'clear': () => {
      return null;
    },
    'all': () => [
      ...commandList.about(),
      "\n",
      ...commandList.skills(),
      "\n",
      ...commandList.experience(),
      "\n",
      ...commandList.education(),
      "\n",
      /*
      ...commandList.projects(),
      "\n",
      */
      ...commandList.contact()
    ]
  };

    if (!commandList[cmd]) {
      return [`Command '${cmd}' not recognized. Type 'help' to see the available commands.`];
    }
    
    return commandList[cmd]();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!input.trim()) return;
    
    const trimmedInput = input.trim().toLowerCase();
    
    if (trimmedInput) {
      setCommandHistory(prev => {
        if (prev.length === 0 || prev[prev.length - 1] !== trimmedInput) {
          return [...prev, trimmedInput];
        }
        return prev;
      });
      setHistoryIndex(-1); 
    }
    
    if (trimmedInput === 'clear') {
      setHistory([...initialHistory]);
      setInput('');
      return;
    }
    
    const newHistory = [
      ...history,
      { type: 'input', text: `guest@pati.im:~$ ${input}` },
    ];
    
    const output = handleCommand(trimmedInput);
    
    if (output) {
      output.forEach(line => {
        newHistory.push({ type: 'output', text: line });
      });
    }
    
    setHistory(newHistory);
    setInput('');
    
    setTimeout(() => {
      if (terminalRef.current) {
        terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
      }
    }, 0);
  };
  
  const handleTabCompletion = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault(); 
      
      const inputValue = input.trim().toLowerCase();
      
      if (inputValue) {
        const matchingCommands = availableCommands.filter(cmd => 
          cmd.startsWith(inputValue)
        );
        
        if (matchingCommands.length === 1) {
          setInput(matchingCommands[0]);
        } else if (matchingCommands.length > 1) {
          const newHistory = [
            ...history,
            { type: 'input', text: `guest@pati.im:~$ ${input}` },
            { type: 'output', text: matchingCommands.join('  ') }
          ];
          
          setHistory(newHistory);
          const commonPrefix = findCommonPrefix(matchingCommands);
          if (commonPrefix.length > inputValue.length) {
            setInput(commonPrefix);
          }
        }
      }
    }
  };
  
  const findCommonPrefix = (strings) => {
    if (!strings.length) return '';
    if (strings.length === 1) return strings[0];
    
    let prefix = strings[0];
    for (let i = 1; i < strings.length; i++) {
      while (strings[i].indexOf(prefix) !== 0) {
        prefix = prefix.substring(0, prefix.length - 1);
        if (prefix === '') return '';
      }
    }
    return prefix;
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      
      if (commandHistory.length > 0) {
        const newIndex = historyIndex < commandHistory.length - 1 
          ? historyIndex + 1 
          : commandHistory.length - 1;
        
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex]);
      }
    } 
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput('');
      }
    }
    else if (e.key === 'Tab') {
      handleTabCompletion(e);
    }
  };

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  const handleTerminalClick = () => {
    inputRef.current.focus();
  };
  
  return (
    <div className="page-background">
      <div className="terminal-container">
        <div className="terminal">
          {/* Terminal header */}
          <div className="terminal-header">
            <div className="buttons-container">
              <div className="button-red"></div>
              <div className="button-yellow"></div>
              <div className="button-green"></div>
            </div>
            <div className={`header-title ${isMobile ? 'mobile-header-title' : ''}`}>
              guest@pati.im:~
            </div>
            {isMobile && (
              <div style={{
                width: '16px',
                visibility: 'hidden'
              }}></div>
            )}
          </div>
          
          {/* Terminal output */}
          <div 
            ref={terminalRef}
            className="terminal-body"
            onClick={handleTerminalClick}
          >
            {history.map((item, index) => (
              <div 
                key={index} 
                className={
                  item.type === 'input' 
                    ? 'input-text' 
                    : (item.italic ? 'output-text-italic' : 'output-text')
                }
              >
                <pre 
                  className={item.italic ? 'pre-italic' : 'pre'}
                  dangerouslySetInnerHTML={{ __html: item.text }}
                ></pre>
              </div>
            ))}
            
            <form onSubmit={handleSubmit} className="command-form">
              <span className="prompt">guest@pati.im:~$&nbsp;</span>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="terminal-input"
                spellCheck="false"
                autoComplete="off"
                autoCapitalize="none"
              />
            </form>
          </div>
          
          <div className="terminal-footer">
            {isMobile ? (
              <div className="mobile-footer">
                <span style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>{bioData.name}</span>
                <span>v1.0.0</span>
              </div>
            ) : (
              <>
                <span>{bioData.name} | {bioData.title}</span>
                <span>v1.0.0</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}